import { membershipIdFromFrameShape, normalizeLogicalFrameId } from './frameMembership.js';

function isAnnotationShape(record) {
  return (
    record?.typeName === 'shape'
    && (record.type === 'frame' || record.type === 'arrow')
  );
}

/** Count frames + arrows in a document snapshot (pins excluded). */
export function countSnapshotAnnotations(documentSnapshot) {
  const store = documentSnapshot?.store;
  if (!store || typeof store !== 'object') return 0;
  let count = 0;
  for (const record of Object.values(store)) {
    if (isAnnotationShape(record)) count += 1;
  }
  return count;
}

/** Count arrow bindings (connect-line endpoints) in a document snapshot. */
export function countSnapshotArrowBindings(documentSnapshot) {
  const store = documentSnapshot?.store;
  if (!store || typeof store !== 'object') return 0;
  let count = 0;
  for (const record of Object.values(store)) {
    if (record?.typeName === 'binding' && record?.type === 'arrow') count += 1;
  }
  return count;
}

/** Live frame/arrow count on the current page. */
export function countEditorAnnotations(editor) {
  if (!editor?.getCurrentPageShapes) return 0;
  return editor
    .getCurrentPageShapes()
    .filter(shape => shape?.type === 'frame' || shape?.type === 'arrow').length;
}

/**
 * Reject snapshots that would wipe frames/arrows while the editor still has them
 * (bad extract / teardown race).
 *
 * @param {{ allowClear?: boolean }} [options]
 *   When `allowClear` is true (user deleted the last annotation), an empty
 *   extract may replace a non-empty previous snapshot. Unload / debounce paths
 *   must leave this false so teardown (liveCount 0) cannot wipe the server.
 */
export function shouldPersistBoardSnapshot(
  editor,
  documentSnapshot,
  previousSnapshot,
  options = {}
) {
  if (!documentSnapshot?.store) return false;
  const nextCount = countSnapshotAnnotations(documentSnapshot);
  const liveCount = countEditorAnnotations(editor);
  const prevCount = countSnapshotAnnotations(previousSnapshot);
  const nextBindings = countSnapshotArrowBindings(documentSnapshot);
  const prevBindings = countSnapshotArrowBindings(previousSnapshot);
  const allowClear = Boolean(options.allowClear);

  // Never persist an extract emptier than the live editor.
  if (nextCount < liveCount) return false;
  if (nextCount === 0 && liveCount > 0) return false;

  // Teardown / pagehide often reports liveCount 0 after the store is cleared.
  // Do not let that wipe a known-good snapshot unless the caller opted in.
  if (!allowClear && prevCount > 0 && nextCount < prevCount && liveCount === 0) {
    return false;
  }
  if (!allowClear && prevBindings > 0 && nextBindings < prevBindings && liveCount === 0) {
    return false;
  }

  // Prefer the richer snapshot when an in-flight stale extract is emptier than
  // both the live editor and the last known good save.
  if (nextCount < prevCount && nextCount < liveCount) return false;

  return true;
}

/** Prefer the snapshot that still has frames/arrows/bindings when reloading the same board. */
export function preferRicherAnnotationSnapshot(localSnapshot, serverSnapshot) {
  const localCount = countSnapshotAnnotations(localSnapshot);
  const serverCount = countSnapshotAnnotations(serverSnapshot);
  if (localCount > serverCount) return localSnapshot;
  if (serverCount > localCount) return serverSnapshot ?? localSnapshot ?? null;
  // Same shape count — keep the side that still has connect-line bindings.
  const localBindings = countSnapshotArrowBindings(localSnapshot);
  const serverBindings = countSnapshotArrowBindings(serverSnapshot);
  if (localBindings > serverBindings) return localSnapshot;
  return serverSnapshot ?? localSnapshot ?? null;
}

/**
 * Reject placement writes that would wipe all pins (partial sync / teardown).
 * Intentional deletes update local placements first, so prev/next stay aligned.
 */
export function shouldPersistPlacements(nextPlacements, previousPlacements) {
  const next = Array.isArray(nextPlacements) ? nextPlacements : [];
  const prev = Array.isArray(previousPlacements) ? previousPlacements : [];
  if (next.length === 0 && prev.length > 0) return false;
  return true;
}

/**
 * Prefer the placement list that still has more pins when a force reload races
 * an in-flight placements PATCH.
 */
export function preferRicherPlacements(localPlacements, serverPlacements) {
  const local = Array.isArray(localPlacements) ? localPlacements : [];
  const server = Array.isArray(serverPlacements) ? serverPlacements : [];
  if (local.length > server.length) return local;
  if (server.length > local.length) return server;
  return server.length ? server : local;
}

/**
 * On force reload of the same board: keep local coords for pins the user may
 * have just moved (in-flight PATCH), and take server-only entries for newly
 * synced pins.
 */
function isInboxPlacementSource(source) {
  const next = String(source || 'inbox');
  return next === 'inbox' || next === '';
}

function isSettledPlacementSource(source) {
  const next = String(source || '');
  return next === 'ai' || next === 'preferred' || next === 'user';
}

export function mergePreferLocalPlacements(localPlacements, serverPlacements) {
  const local = Array.isArray(localPlacements) ? localPlacements : [];
  const server = Array.isArray(serverPlacements) ? serverPlacements : [];
  if (!local.length) return server;
  if (!server.length) return local;

  const localById = new Map(local.map(p => [String(p.activityId), p]));
  const serverById = new Map(server.map(p => [String(p.activityId), p]));
  const serverIds = new Set(serverById.keys());
  const merged = [];

  for (const placement of local) {
    const id = String(placement.activityId);
    // Drop local entries removed on the server (pin deleted elsewhere).
    if (!serverIds.has(id)) continue;
    const remote = serverById.get(id);
    // pin_placed force-reload used to keep stale local inbox over server ai,
    // then write that inbox back. Take the settled server row instead.
    if (
      remote
      && isInboxPlacementSource(placement?.placementSource)
      && isSettledPlacementSource(remote?.placementSource)
    ) {
      merged.push(remote);
      continue;
    }
    merged.push(placement);
  }
  for (const placement of server) {
    const id = String(placement.activityId);
    if (localById.has(id)) continue;
    merged.push(placement);
  }
  return merged;
}

/**
 * Shape-derived placements win for visible pins; keep prior entries for activityIds
 * missing from the editor (partial pin sync must not drop settled positions).
 */
export function mergePlacementsFromShapes(shapePlacements, existingPlacements = []) {
  const fromShapes = new Map(
    (shapePlacements || [])
      .filter(p => p?.activityId != null)
      .map(p => [String(p.activityId), p])
  );
  const merged = [...fromShapes.values()];
  for (const prev of existingPlacements || []) {
    const id = String(prev?.activityId || '');
    if (!id || fromShapes.has(id)) continue;
    merged.push(prev);
  }
  return merged;
}

export function framesFromEditor(editor) {
  if (!editor?.getCurrentPageShapes) return [];
  const frames = [];
  for (const shape of editor.getCurrentPageShapes()) {
    if (!shape || shape.type !== 'frame') continue;
    const id = membershipIdFromFrameShape(shape);
    if (!id) continue;
    const bounds = editor.getShapePageBounds?.(shape);
    frames.push({
      id,
      x: bounds?.x ?? shape.x ?? 0,
      y: bounds?.y ?? shape.y ?? 0,
      w: bounds?.w ?? shape.props?.w ?? 0,
      h: bounds?.h ?? shape.props?.h ?? 0,
      label: String(shape.props?.name || '').trim(),
    });
  }
  return frames;
}

/**
 * Recreate missing frame shapes from board.frames when the tldraw snapshot
 * omitted them (stale/partial save). Returns true if any shape was created.
 */
export function ensureFramesFromBoardMeta(editor, frames = []) {
  if (!editor || !frames?.length) return false;

  const existing = new Set(
    (editor.getCurrentPageShapes?.() || [])
      .filter(shape => shape?.type === 'frame')
      .map(shape => membershipIdFromFrameShape(shape))
      .filter(Boolean)
  );

  let created = false;
  for (const frame of frames) {
    const logicalId = normalizeLogicalFrameId(frame?.id);
    if (!logicalId || existing.has(logicalId)) continue;

    const w = Number(frame.w) || 400;
    const h = Number(frame.h) || 300;
    const x = Number(frame.x) || 0;
    const y = Number(frame.y) || 0;
    const name = String(frame.label || '').trim() || 'Frame';

    if (logicalId.startsWith('shape:')) {
      if (editor.getShape(logicalId)) {
        existing.add(logicalId);
        continue;
      }
      editor.createShape({
        id: logicalId,
        type: 'frame',
        x,
        y,
        props: { w, h, name },
        meta: { boardFrameId: logicalId },
      });
    } else {
      const shapeId = `shape:frame-${logicalId}`;
      if (editor.getShape(shapeId)) {
        existing.add(logicalId);
        continue;
      }
      editor.createShape({
        id: shapeId,
        type: 'frame',
        x,
        y,
        props: { w, h, name },
        meta: { boardFrameId: logicalId },
      });
    }

    existing.add(logicalId);
    created = true;
  }

  return created;
}
